﻿import { computed, makeObservable } from "mobx";
import type { SourceInfoModel } from "../../../../OpenApi/api";
import type { ISessionStore } from "../../session/SessionStore";

export interface IUploadSourcesStore {
	/**
	 * Returns a list of sources that have an upload portal available.
	 */
	readonly sourcesWithUploadPortal: SourceInfoModel[];

	/**
	 * Returns a list of sources that have an upload portal available and where the user has permission to generate upload codes
	 */
	readonly ableToGenerateForSources: SourceInfoModel[];

	/**
	 * Returns a list of sources that have an upload portal available and where the user is a source user or source admin
	 */
	readonly sourcesWithUploadPortalToShow: SourceInfoModel[];
}

export class UploadSourcesStore implements IUploadSourcesStore {
	constructor(private readonly sessionStore: ISessionStore) {
		makeObservable(this);
	}

	@computed
	public get sourcesWithUploadPortal(): SourceInfoModel[] {
		const { sessionStore } = this;
		const { currentUserSettings } = sessionStore;
		if (!currentUserSettings) return [];

		const { dicomUploadSettings } = currentUserSettings;
		const { sourcesWhereUploadingStudiesIsAllowed } = dicomUploadSettings;
		const sourcesWithUploadPortal = sourcesWhereUploadingStudiesIsAllowed.slice(0);
		sourcesWithUploadPortal.sort((a, b) => {
			return (a.description ?? "").toLowerCase().localeCompare((b.description ?? "").toLowerCase());
		});
		return sourcesWithUploadPortal;
	}

	@computed
	public get ableToGenerateForSources(): SourceInfoModel[] {
		const { sessionStore, sourcesWithUploadPortal } = this;
		const { currentUserSettings } = sessionStore;
		if (!currentUserSettings) return [];

		const { dicomUploadSettings } = currentUserSettings;
		const { sourceIdsWhereUserCanGenerateUploadCodes } = dicomUploadSettings;
		return sourcesWithUploadPortal.filter(s => sourceIdsWhereUserCanGenerateUploadCodes.includes(s.id));
	}

	@computed
	public get sourcesWithUploadPortalToShow(): SourceInfoModel[] {
		const { sessionStore, sourcesWithUploadPortal } = this;
		const { currentUserSettings } = sessionStore;
		if (!currentUserSettings) return [];

		const { dicomUploadSettings } = currentUserSettings;
		const { sourceIdsWhereUserIsSourceUserOrSourceAdmin } = dicomUploadSettings;
		return sourcesWithUploadPortal.filter(s => sourceIdsWhereUserIsSourceUserOrSourceAdmin.includes(s.id));
	}
}
